import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useSnackbar } from 'notistack';

// Define the Settings interface
export interface SalonSettings {
  salonName: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  logoUrl?: string; // URL to the logo in Firebase Storage
  currency: {
    code: string;  // 'USD', 'EUR', etc.
    symbol: string; // '$', '€', etc.
    position: 'before' | 'after'; // Where to put the currency symbol
  };
  taxRate: number // Store as decimal, e.g., 0.07 for 7%
}

// Default settings
export const defaultSettings: SalonSettings = {
  salonName: 'Glamour Salon',
  address: '123 Beauty Street, Salon City',
  phone: '(555) 123-4567',
  email: 'info@glamoursalon.com',
  website: 'www.glamoursalon.com',
  logoUrl: '',
  currency: {
    code: 'USD',
    symbol: '$',
    position: 'before',
  },
  taxRate: 0.07 // 7% tax rate
};

// Define context types
interface SettingsContextType {
  settings: SalonSettings;
  loading: boolean;
  updateSettings: (newSettings: Partial<SalonSettings>) => Promise<void>;
  formatCurrency: (amount: number) => string;
}

// Create the context
const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Provider component
export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SalonSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  // Load settings from Firestore on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const settingsDocRef = doc(db, 'settings', 'salonSettings');
        const settingsDoc = await getDoc(settingsDocRef);
        
        if (settingsDoc.exists()) {
          setSettings(settingsDoc.data() as SalonSettings);
        } else {
          // Initialize with default settings if not exist
          await setDoc(settingsDocRef, defaultSettings);
          setSettings(defaultSettings);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        enqueueSnackbar('Failed to load settings. Using defaults.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [enqueueSnackbar]);

  // Update settings in Firestore and state
  const updateSettings = async (newSettings: Partial<SalonSettings>) => {
    try {
      const updatedSettings = { ...settings, ...newSettings };
      const settingsDocRef = doc(db, 'settings', 'salonSettings');
      await setDoc(settingsDocRef, updatedSettings);
      setSettings(updatedSettings);
      enqueueSnackbar('Settings updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating settings:', error);
      enqueueSnackbar('Failed to update settings', { variant: 'error' });
      throw error;
    }
  };

  // Format currency based on settings
  const formatCurrency = (amount: number): string => {
    try {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: settings.currency.code,
        minimumFractionDigits: 2,
      });
      
      return formatter.format(amount);
    } catch (error) {
      // Fallback formatting if Intl formatter fails
      const formattedAmount = amount.toFixed(2);
      return settings.currency.position === 'before' 
        ? `${settings.currency.symbol}${formattedAmount}`
        : `${formattedAmount}${settings.currency.symbol}`;
    }
  };

  const contextValue: SettingsContextType = {
    settings,
    loading,
    updateSettings,
    formatCurrency,
  };

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook to use the settings context
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}; 